'use client'

import { useState } from 'react';
import { usePostCreationStore } from '@/lib/stores/postCreationStore';
import { 
  PostType, 
  TechnicalLevel, 
  ContentTemperature, 
  Sentiment,
  UseCase
} from '@/lib/types/posts';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { format } from 'date-fns';
import { CalendarIcon, Clock, Tag, X, ThermometerSun, Target, Brain, Shield } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';
import { PostRelationshipManager } from './PostRelationshipManager';

export function MetadataEditor() {
  const { currentPost, selectedPostType, updatePostField, updateMetadataField } = usePostCreationStore();
  const [newTag, setNewTag] = useState('');
  const [newKeyTakeaway, setNewKeyTakeaway] = useState('');
  const [newTargetAudience, setNewTargetAudience] = useState('');
  const [date, setDate] = useState<Date>();

  const metadata = currentPost.metadata || {};
  const tags = metadata.tags || [];
  const key_takeaways = currentPost.key_takeaways || [];
  const target_audience = currentPost.target_audience || [];

  const handleAddTag = () => {
    if (!newTag.trim()) return;
    updateMetadataField(['tags'], [...tags, newTag.trim()]);
    setNewTag('');
  };

  const handleRemoveTag = (index: number) => {
    const newTags = [...tags];
    newTags.splice(index, 1);
    updateMetadataField(['tags'], newTags);
  };

  const handleAddKeyTakeaway = () => {
    if (!newKeyTakeaway.trim()) return;
    updatePostField('key_takeaways', [...key_takeaways, newKeyTakeaway.trim()]);
    setNewKeyTakeaway('');
  };

  const handleRemoveKeyTakeaway = (index: number) => {
    const newKeyTakeaways = [...key_takeaways];
    newKeyTakeaways.splice(index, 1);
    updatePostField('key_takeaways', newKeyTakeaways);
  };

  const handleAddTargetAudience = () => {
    if (!newTargetAudience.trim()) return;
    updatePostField('target_audience', [...target_audience, newTargetAudience.trim()]);
    setNewTargetAudience('');
  };

  const handleRemoveTargetAudience = (index: number) => {
    const newTargetAudience = [...target_audience];
    newTargetAudience.splice(index, 1);
    updatePostField('target_audience', newTargetAudience);
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold mb-4">Essential Metadata</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="excerpt">Excerpt</Label>
            <Textarea
              id="excerpt"
              placeholder="Brief summary or introduction"
              value={currentPost.excerpt || ''}
              onChange={(e) => updatePostField('excerpt', e.target.value)}
              className="resize-none h-24"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="summary">Summary</Label>
            <Textarea
              id="summary"
              placeholder="Full summary of post content"
              value={currentPost.summary || ''}
              onChange={(e) => updatePostField('summary', e.target.value)}
              className="resize-none h-24"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="reading-time">Reading Time (minutes)</Label>
            <Input
              id="reading-time"
              type="number"
              min={1}
              placeholder="Estimated reading time"
              value={currentPost.reading_time || ''}
              onChange={(e) => updatePostField('reading_time', parseInt(e.target.value) || 0)}
            />
          </div>
          
          <div className="space-y-2">
            <Label>Schedule Publication</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !date && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, "PPP") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
      
      <div>
        <h2 className="text-xl font-semibold mb-4">Tags</h2>
        <div className="flex flex-wrap gap-2 mb-4">
          {tags.map((tag, index) => (
            <div
              key={index}
              className="bg-secondary text-secondary-foreground px-3 py-1 rounded-full flex items-center"
            >
              <span>{tag}</span>
              <Button
                size="sm"
                variant="ghost"
                className="h-5 w-5 p-0 ml-1"
                onClick={() => handleRemoveTag(index)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="Add a tag..."
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
          />
          <Button variant="outline" onClick={handleAddTag}>
            <Tag className="h-4 w-4 mr-2" />
            Add
          </Button>
        </div>
      </div>
      
      <div>
        <h2 className="text-xl font-semibold mb-4">Content Classification</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="complexity">Content Complexity</Label>
            <Select
              value={String(currentPost.complexity_score || '')}
              onValueChange={(value) => updatePostField('complexity_score', parseInt(value))}
            >
              <SelectTrigger id="complexity">
                <SelectValue placeholder="Select complexity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 - Very Simple</SelectItem>
                <SelectItem value="2">2 - Simple</SelectItem>
                <SelectItem value="3">3 - Moderate</SelectItem>
                <SelectItem value="4">4 - Complex</SelectItem>
                <SelectItem value="5">5 - Very Complex</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="technical-level">Technical Level</Label>
            <Select
              value={currentPost.technical_level || ''}
              onValueChange={(value) => updatePostField('technical_level', value as TechnicalLevel)}
            >
              <SelectTrigger id="technical-level">
                <SelectValue placeholder="Select technical level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TechnicalLevel.BEGINNER}>Beginner</SelectItem>
                <SelectItem value={TechnicalLevel.INTERMEDIATE}>Intermediate</SelectItem>
                <SelectItem value={TechnicalLevel.ADVANCED}>Advanced</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="content-temperature">Content Temperature</Label>
            <Select
              value={currentPost.content_temperature || ''}
              onValueChange={(value) => updatePostField('content_temperature', value as ContentTemperature)}
            >
              <SelectTrigger id="content-temperature">
                <SelectValue placeholder="Select content temperature" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ContentTemperature.EVERGREEN}>
                  <div className="flex items-center">
                    <ThermometerSun className="h-4 w-4 mr-2" />
                    Evergreen
                  </div>
                </SelectItem>
                <SelectItem value={ContentTemperature.TIME_SENSITIVE}>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-2" />
                    Time-Sensitive
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confidence-score">Content Confidence</Label>
            <Select
              value={String(currentPost.confidence_score || '')}
              onValueChange={(value) => updatePostField('confidence_score', parseInt(value))}
            >
              <SelectTrigger id="confidence-score">
                <SelectValue placeholder="Select confidence level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 - Low Confidence</SelectItem>
                <SelectItem value="2">2 - Somewhat Confident</SelectItem>
                <SelectItem value="3">3 - Confident</SelectItem>
                <SelectItem value="4">4 - Very Confident</SelectItem>
                <SelectItem value="5">5 - Highly Authoritative</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sentiment">Content Sentiment</Label>
            <Select
              value={currentPost.sentiment || ''}
              onValueChange={(value) => updatePostField('sentiment', value as Sentiment)}
            >
              <SelectTrigger id="sentiment">
                <SelectValue placeholder="Select sentiment" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={Sentiment.POSITIVE}>Positive</SelectItem>
                <SelectItem value={Sentiment.NEUTRAL}>Neutral</SelectItem>
                <SelectItem value={Sentiment.NEGATIVE}>Negative</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="use-case">Primary Use Case</Label>
            <Select
              value={metadata.use_case || ''}
              onValueChange={(value) => updateMetadataField(['use_case'], value as UseCase)}
            >
              <SelectTrigger id="use-case">
                <SelectValue placeholder="Select use case" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UseCase.EDUCATIONAL}>Educational</SelectItem>
                <SelectItem value={UseCase.DECISION_SUPPORT}>Decision Support</SelectItem>
                <SelectItem value={UseCase.NEWS}>News</SelectItem>
                <SelectItem value={UseCase.ANALYSIS}>Analysis</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-xl font-semibold mb-4">Key Takeaways</h2>
        <div className="space-y-4">
          {key_takeaways.map((takeaway, index) => (
            <div key={index} className="flex items-start gap-2">
              <div className="rounded-md border p-3 flex-1">
                {takeaway}
              </div>
              <Button
                size="sm"
                variant="outline"
                className="shrink-0"
                onClick={() => handleRemoveKeyTakeaway(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <div className="flex gap-2">
            <Textarea
              placeholder="Add a key takeaway..."
              value={newKeyTakeaway}
              onChange={(e) => setNewKeyTakeaway(e.target.value)}
              className="resize-none"
            />
            <Button variant="outline" onClick={handleAddKeyTakeaway} className="shrink-0">
              Add
            </Button>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-xl font-semibold mb-4">Target Audience</h2>
        <div className="flex flex-wrap gap-2 mb-4">
          {target_audience.map((audience, index) => (
            <div
              key={index}
              className="bg-secondary text-secondary-foreground px-3 py-1 rounded-full flex items-center"
            >
              <Target className="h-4 w-4 mr-2" />
              <span>{audience}</span>
              <Button
                size="sm"
                variant="ghost"
                className="h-5 w-5 p-0 ml-1"
                onClick={() => handleRemoveTargetAudience(index)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="Add target audience..."
            value={newTargetAudience}
            onChange={(e) => setNewTargetAudience(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddTargetAudience()}
          />
          <Button variant="outline" onClick={handleAddTargetAudience}>
            <Target className="h-4 w-4 mr-2" />
            Add
          </Button>
        </div>
      </div>

      <div>
        <h2 className="text-xl font-semibold mb-4">Action Inspiration</h2>
        <div className="flex items-center space-x-2">
          <Switch
            id="action-inspiration"
            checked={metadata.action_inspiration || false}
            onCheckedChange={(checked) => updateMetadataField(['action_inspiration'], checked)}
          />
          <Label htmlFor="action-inspiration">
            This content might inspire portfolio changes
          </Label>
        </div>
      </div>

      <div className="border-t pt-8 mt-8">
        <h2 className="text-xl font-semibold mb-4">Post Relationships</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Connect this post to other posts to create a network of related content.
        </p>
        <PostRelationshipManager />
      </div>
    </div>
  );
} 